import type { MessageDeliveryStatus } from '../../../lib/types';

/**
 * MessageDeliveryStatusIndicator — r7b §30 ADR-113 pixel-fiel.
 *
 * 7 estados outbound visualizados com ícones SVG inline + classes de cor
 * correspondendo ao design canônico r7b (scenario.jsx messageLifecycleStates):
 *   - `queued`    → clock-3 neutral (opacidade reduzida)
 *   - `sending`   → hourglass ai (spinning msg-spin-icon)
 *   - `sent`      → ✓ neutral
 *   - `delivered` → ✓✓ neutral
 *   - `read`      → ✓✓ info (azul)
 *   - `failed`    → alert danger (vermelho) + botão "Detalhes"
 *   - `retrying`  → rotate-cw ai (spinning) + badge N/M
 */
export function MessageDeliveryStatusIndicator({
  status,
  onRetry,
  failureReason,
  retryAttempt,
  retryMax,
  onDetails,
}: {
  status: MessageDeliveryStatus;
  onRetry?: () => void;
  failureReason?: string | null;
  retryAttempt?: number | null;
  retryMax?: number | null;
  /** Abre MessageFailedModal para detalhes completos (opcional). */
  onDetails?: () => void;
}): JSX.Element {
  if (status === 'failed') {
    return (
      <span
        className="msg-status msg-status--danger"
        data-testid="msg-status-failed"
        title={failureReason ?? 'Falha ao enviar'}
      >
        <IconAlert />
        <span>Falhou</span>
        {onDetails !== undefined ? (
          <button
            type="button"
            onClick={onDetails}
            className="underline hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-current rounded px-0.5"
          >
            Detalhes
          </button>
        ) : onRetry !== undefined ? (
          <button
            type="button"
            onClick={onRetry}
            className="underline hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-current rounded px-0.5"
          >
            Tentar novamente
          </button>
        ) : null}
      </span>
    );
  }

  if (status === 'retrying') {
    return (
      <span
        className="msg-status msg-status--ai"
        aria-label="Re-tentando envio…"
        title="Re-tentativa em andamento"
        data-testid="msg-status-retrying"
      >
        <IconRotateCw />
        {retryAttempt !== undefined &&
        retryAttempt !== null &&
        retryMax !== undefined &&
        retryMax !== null ? (
          <span className="msg-retry-badge">
            {retryAttempt}/{retryMax}
          </span>
        ) : null}
      </span>
    );
  }

  if (status === 'queued') {
    return (
      <span
        className="msg-status msg-status--neutral opacity-60"
        aria-label="Aguardando envio"
        title="Aguardando envio (queued)"
        data-testid="msg-status-queued"
      >
        <IconClock />
      </span>
    );
  }

  if (status === 'sending') {
    return (
      <span
        className="msg-status msg-status--ai"
        aria-label="Enviando…"
        title="Enviando ao provedor"
        data-testid="msg-status-sending"
      >
        <IconHourglass />
      </span>
    );
  }

  const config = STATUS_CONFIG[status];
  return (
    <span
      className={`msg-status msg-status--${config.colorClass}`}
      aria-label={config.label}
      title={config.tooltip}
      data-testid={`msg-status-${status}`}
    >
      {config.icon}
    </span>
  );
}

/* ─── Icon SVGs compactos (14×14) ─── */

function IconClock(): JSX.Element {
  return (
    <svg
      className="icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconHourglass(): JSX.Element {
  return (
    <svg
      className="icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 22h14" />
      <path d="M5 2h14" />
      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
    </svg>
  );
}

function IconCheck(): JSX.Element {
  return (
    <svg
      className="icon"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconCheckDouble(): JSX.Element {
  return (
    <svg
      className="icon"
      width="14"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="17 4 7 15 2 10" />
      <polyline points="22 4 12 15 7 10" />
    </svg>
  );
}

function IconAlert(): JSX.Element {
  return (
    <svg
      className="icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function IconRotateCw(): JSX.Element {
  return (
    <svg
      className="icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 2v6h-6" />
      <path d="M21 13a9 9 0 1 1-3-7.7L21 8" />
    </svg>
  );
}

/* ─── Status config map (sent / delivered / read) ─── */

const STATUS_CONFIG: Record<
  Extract<MessageDeliveryStatus, 'sent' | 'delivered' | 'read'>,
  { label: string; tooltip: string; colorClass: string; icon: JSX.Element }
> = {
  sent: {
    label: 'Enviada',
    tooltip: 'Enviado ao provedor',
    colorClass: 'neutral',
    icon: <IconCheck />,
  },
  delivered: {
    label: 'Entregue',
    tooltip: 'Entregue ao destinatário',
    colorClass: 'neutral',
    icon: <IconCheckDouble />,
  },
  read: {
    label: 'Lida',
    tooltip: 'Lido pelo destinatário',
    colorClass: 'info',
    icon: <IconCheckDouble />,
  },
};
